/* eslint-disable react/jsx-max-props-per-line */
/**
 * Tabela em card (WidgetV2) para formulários — substitui o uso de `components/Table`
 * em containers V2, sem Redux nem paginação por API.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  CardActions,
  DialogContentText,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';

import WidgetV2 from './WidgetV2';
import DialogDefaultV2 from './DialogDefaultV2';
import EmptyState from '../Table/EmptyState';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const getTitleCells = (titles, hasRowActions) => {
  const cells = titles.map((t, i) => <TableCell key={i}>{t}</TableCell>);
  if (hasRowActions) {
    cells.push(<TableCell key="actions">Ações</TableCell>);
  }
  return cells;
};

const TableWidgetV2 = ({
  rows,
  titles,
  headerTitle,
  deleteMessage,
  renderRows,
  hasRowActions,
  onEdit,
  onDelete,
  onAdd,
  actions,
  hasAdd,
  addTitle,
  collapse,
  initialExpanded,
  showDelete,
  showDeleteRow,
  showEdit,
  showView,
  onView,
  showEditRow,
  showViewRow,
  size,
  idAsList,
  defaultRowsPerPage,
  showEmptyState,
  emptyStateTitle,
  emptyStateMessage,
  onClearFilters,
  showError,
  widgetSubheader,
}) => {
  const theme = useTheme();
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [localRows, setLocalRows] = useState(() => rows || []);

  useEffect(() => {
    setLocalRows(rows || []);
  }, [rows]);

  const totalRows = localRows.length;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
    setPage((p) => Math.min(p, maxPage));
  }, [totalRows, rowsPerPage]);
  const paginatedRows = localRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handleConfirmDelete = () => {
    onDelete(deleteId, () => {});
    handleCloseConfirm();
  };

  const getRowId = (row) => (idAsList ? row[0] : row.id);

  const getIdFromEvent = (e) =>
    e.currentTarget.closest('tr').getAttribute('id');

  const handlerEdit = (e) => onEdit(getIdFromEvent(e));
  const handlerView = (e) => onView(getIdFromEvent(e));
  const handlerDelete = (e) => {
    setDeleteId(getIdFromEvent(e));
    setConfirmOpen(true);
  };

  const iconButtonSx = {
    p: 0.375,
    color: 'text.secondary',
    '&:hover': { bgcolor: 'action.hover' },
  };

  const getActions = (row) =>
    (actions || []).map((a) =>
      !a.show || a.show(row) ? (
        <IconButton
          key={a.key}
          onClick={(e) => a.onClick(getIdFromEvent(e))}
          size="small"
          sx={iconButtonSx}
        >
          {a.icon}
        </IconButton>
      ) : null
    );

  const headerActions = [];
  if (hasAdd) {
    headerActions.push({ title: addTitle, onClick: onAdd });
  }

  /** Zebra alinhada a `TableV2` (DataGrid): índice par → neutral mais claro, ímpar → mais escuro. */
  const rowBackground = (index) => {
    const isEven = index % 2 === 0;
    if (theme.palette.mode === 'dark') {
      return isEven ? theme.palette.neutral[900] : theme.palette.neutral[800];
    }
    return isEven ? theme.palette.neutral[50] : theme.palette.neutral[100];
  };

  const renderRow = (r, index) => (
    <TableRow
      hover
      key={String(getRowId(r))}
      id={String(getRowId(r))}
      sx={{
        bgcolor: rowBackground(index),
        '&:hover': {
          bgcolor: 'action.hover',
        },
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest,
        }),
      }}
    >
      {renderRows(r)}
      {hasRowActions ? (
        <TableCell size="small">
          <Grid container spacing={2}>
            {showEdit && (!showEditRow || showEditRow(r)) && (
              <IconButton
                size="small"
                sx={iconButtonSx}
                onClick={handlerEdit}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {showView && (!showViewRow || showViewRow(r)) && (
              <IconButton
                size="small"
                sx={iconButtonSx}
                onClick={handlerView}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {getActions(r)}
            {showDelete && (!showDeleteRow || showDeleteRow(r)) && (
              <IconButton
                size="small"
                sx={iconButtonSx}
                onClick={handlerDelete}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            )}
          </Grid>
        </TableCell>
      ) : null}
    </TableRow>
  );

  const showEmpty = showEmptyState && totalRows === 0;

  return (
    <>
      <WidgetV2
        title={headerTitle}
        actions={headerActions}
        collapse={collapse}
        initialExpanded={initialExpanded}
        subheader={widgetSubheader}
      >
        {showEmpty ? (
          <Box
            sx={{
              p: 4,
              bgcolor: 'background.paper',
            }}
          >
            <EmptyState
              title={emptyStateTitle}
              message={emptyStateMessage}
              onClearFilters={onClearFilters}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ bgcolor: 'background.paper', width: '100%' }}>
              <PerfectScrollbar>
                <Table size={size}>
                  {/*
                    Cores de thead vêm de theme_v2 (MuiTableHead + MuiTableCell).
                    Reforçamos só peso da fonte para alinhar ao DataGrid em TableV2.
                  */}
                  <TableHead
                    sx={{
                      '& .MuiTableCell-head': {
                        fontWeight: 600,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableRow>{getTitleCells(titles, hasRowActions)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((r, index) => renderRow(r, index))}
                  </TableBody>
                </Table>
              </PerfectScrollbar>
            </Box>
            <CardActions
              sx={{
                justifyContent: 'flex-end',
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 15, 25]}
                labelRowsPerPage="Linhas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
              />
            </CardActions>
          </>
        )}
        {showError && (
          <Box
            component="p"
            sx={{
              color: 'error.main',
              mx: 1.5,
              my: 1,
              minHeight: '1em',
              typography: 'caption',
            }}
          >
            *** É necessario adicionar ao menos um item.
          </Box>
        )}
      </WidgetV2>
      <DialogDefaultV2
        open={confirmOpen}
        handlerCancel={handleCloseConfirm}
        handlerConfirm={handleConfirmDelete}
        title="Você tem certeza?"
        confirmMessage="Deletar"
      >
        <DialogContentText>{deleteMessage}</DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

TableWidgetV2.propTypes = {
  rows: PropTypes.array.isRequired,
  titles: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerTitle: PropTypes.string,
  deleteMessage: PropTypes.string,
  renderRows: PropTypes.func.isRequired,
  hasRowActions: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  actions: PropTypes.array,
  hasAdd: PropTypes.bool,
  addTitle: PropTypes.string,
  collapse: PropTypes.bool,
  initialExpanded: PropTypes.bool,
  showDelete: PropTypes.bool,
  showDeleteRow: PropTypes.func,
  showEdit: PropTypes.bool,
  showView: PropTypes.bool,
  onView: PropTypes.func,
  showEditRow: PropTypes.func,
  showViewRow: PropTypes.func,
  size: PropTypes.string,
  idAsList: PropTypes.bool,
  defaultRowsPerPage: PropTypes.number,
  showEmptyState: PropTypes.bool,
  emptyStateTitle: PropTypes.string,
  emptyStateMessage: PropTypes.string,
  onClearFilters: PropTypes.func,
  showError: PropTypes.bool,
  widgetSubheader: PropTypes.node,
};

TableWidgetV2.defaultProps = {
  deleteMessage: 'A informação será removida e não poderá ser mais recuperado.',
  hasRowActions: false,
  headerTitle: '',
  onEdit: () => {},
  onDelete: () => {},
  onAdd: () => {},
  actions: [],
  hasAdd: true,
  addTitle: 'Novo',
  collapse: false,
  initialExpanded: true,
  showDelete: true,
  showDeleteRow: null,
  showEdit: true,
  showView: false,
  onView: () => {},
  showEditRow: null,
  showViewRow: null,
  size: 'medium',
  idAsList: false,
  defaultRowsPerPage: 10,
  showEmptyState: true,
  emptyStateTitle: null,
  emptyStateMessage: null,
  onClearFilters: null,
  showError: false,
  widgetSubheader: null,
};

export default TableWidgetV2;
